import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  EditIcon,
  Heading,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  Tooltip,
  PlusIcon,
  TrashIcon,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';

import type { HolidayEntry } from 'api/types/attendanceSettingsTypes';
import type { AttendanceSettingsFormState } from 'components/Settings/AttendanceAndLeavesSettings/types';
import { formatHolidayDate } from 'components/Settings/AttendanceAndLeavesSettings/utils';
import { AddHolidayModal } from './AddHolidayModal';
import { DeleteConfirmModal } from 'components/Settings/AttendanceAndLeavesSettings/components/DeleteConfirmModal';

export const OrgHolidaysTable = () => {
  const { formData, setFieldValue } = useFormWithProvider<AttendanceSettingsFormState>();
  const allHolidays = [...(formData.predefinedHolidays ?? []), ...(formData.customHolidays ?? [])]
    .sort((a, b) => a.date.localeCompare(b.date));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<HolidayEntry | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const openAddModal = () => {
    setEditingHoliday(null);
    setIsModalOpen(true);
  };

  const openEditModal = (holiday: HolidayEntry) => {
    setEditingHoliday(holiday);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingHoliday(null);
  };

  const updateHoliday = (id: number, updated: HolidayEntry) => {
    const inPredefined = (formData.predefinedHolidays ?? []).some((h) => h.id === id);
    if (inPredefined) {
      setFieldValue(
        'predefinedHolidays',
        (formData.predefinedHolidays ?? []).map((holiday) =>
          holiday.id === id ? updated : holiday,
        ),
      );
    } else {
      setFieldValue(
        'customHolidays',
        (formData.customHolidays ?? []).map((holiday) => (holiday.id === id ? updated : holiday)),
      );
    }
  };

  const handleModalSave = (holiday: HolidayEntry) => {
    if (editingHoliday) {
      updateHoliday(editingHoliday.id, { ...holiday, id: editingHoliday.id });
    } else {
      setFieldValue('customHolidays', [...(formData.customHolidays ?? []), holiday]);
    }
    closeModal();
  };

  const removeHoliday = (id: number) => {
    const inPredefined = (formData.predefinedHolidays ?? []).some((h) => h.id === id);
    if (inPredefined) {
      return; // Predefined holidays cannot be removed.
    } else {
      setFieldValue(
        'customHolidays',
        (formData.customHolidays ?? []).filter((h) => h.id !== id),
      );
    }
  };

  const getLocationsList = (holiday: HolidayEntry): string[] => {
    if (holiday.selectedLocations.length === 0) {
      return [];
    }
    return (formData.holidayLocations || [])
      .filter(
        (loc) => holiday.selectedLocations.includes(loc.id.toString()) && loc.id.toString() !== '0',
      )
      .map((loc) => loc.name);
  };

  const describeScope = (holiday: HolidayEntry): string | null => {
    if (holiday.selectedLocations.length === 0) return null;
    if (
      formData.holidayLocations?.length &&
      holiday.selectedLocations.length &&
      (holiday.selectedLocations.length === formData.holidayLocations.length ||
        holiday.selectedLocations.includes('0'))
    ) {
      return 'All';
    }
    return getLocationsList(holiday).join(', ');
  };

  return (
    <Box
      padding="spacing.6"
      borderRadius="medium"
      borderWidth="thin"
      borderColor="surface.border.gray.subtle"
      backgroundColor="surface.background.gray.intense">
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap="spacing.6">
        <Box>
          <Heading size="medium">Organisation Holidays</Heading>
          <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
            Paid holidays for your organisation. Add state-specific holidays where applicable.
          </Text>
        </Box>
        <Button variant="secondary" icon={PlusIcon} iconPosition="left" onClick={openAddModal}>
          Add Holiday
        </Button>
      </Box>

      <Box marginTop="spacing.7">
        <Table
          data={{ nodes: allHolidays }}
          rowDensity="comfortable"
          gridTemplateColumns="1fr 2fr 1fr auto">
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Description</TableHeaderCell>
                  <TableHeaderCell>Applies To</TableHeaderCell>
                  <TableHeaderCell> </TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((holiday: HolidayEntry) => {
                  const scopeDescription = describeScope(holiday);
                  const scopeTooltipContent = getLocationsList(holiday).join(', ');
                  return (
                    <TableRow key={holiday.id} item={holiday}>
                      <TableCell>{formatHolidayDate(holiday.date)}</TableCell>
                      <TableCell>{holiday.description}</TableCell>
                      <TableCell>
                        {scopeDescription ? (
                          <Tooltip content={scopeTooltipContent}>
                            <Badge color="information">{scopeDescription}</Badge>
                          </Tooltip>
                        ) : (
                          '--'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap="spacing.4" alignItems="center">
                          <IconButton
                            accessibilityLabel={`Edit ${holiday.description}`}
                            icon={EditIcon}
                            emphasis="subtle"
                            onClick={() => openEditModal(holiday)}
                          />
                          {holiday.isCustom && (
                            <IconButton
                              accessibilityLabel={`Remove ${holiday.description}`}
                              icon={TrashIcon}
                              emphasis="subtle"
                              onClick={() => setPendingDeleteId(holiday.id)}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </>
          )}
        </Table>
      </Box>

      <AddHolidayModal
        isOpen={isModalOpen}
        onDismiss={closeModal}
        onAdd={handleModalSave}
        initialData={editingHoliday}
        isPredefined={editingHoliday != null && !editingHoliday.isCustom}
      />

      <DeleteConfirmModal
        isOpen={pendingDeleteId !== null}
        entityName="this holiday"
        onDismiss={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId !== null) removeHoliday(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </Box>
  );
};
